"use client";

import { useEffect, useState } from "react";


import Lottie from "lottie-react";
import profoundLogo from "../../images/profound-logo.svg";
import profoundWord from "../../images/profound-word.svg";

import { AppLoadingScreen } from "../../components/app-loading-screen";
import { useWhoAmI, AccountStatus } from "../../hooks/useWhoAmI";
import { useRatingActions, useRatingStatus } from "../../hooks/useCallRating";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { FeedbackModalWrapper } from "../../components/FeedbackModal/FeedbackModalWrapper";

const MySwal = withReactContent(Swal);

export default function WaitlistPageClient() {
  const [animationData, setAnimationData] = useState<any>(null);
  const [showRating, setShowRating] = useState(false);

  const { userData, loading: shouldShowLoader } = useWhoAmI();
  const isVerificationFailed = userData?.account_status === AccountStatus.UNDER_REVIEW;

  useRatingActions();

  const {
    shouldShowRating: backendShouldShow,
    conversationId: backendConversationId,
    loading: statusLoading,
  } = useRatingStatus(true);

  useEffect(() => {
    if (!statusLoading && backendShouldShow && backendConversationId) {
      setShowRating(true);
    }
  }, [statusLoading, backendShouldShow, backendConversationId]);

  useEffect(() => {
    if (showRating && backendConversationId) {
      MySwal.fire({
        html: (
          <FeedbackModalWrapper
            conversationId={backendConversationId}
            onComplete={() => {
              MySwal.close();
              setShowRating(false);
            }}
          />
        ),
        showConfirmButton: false,
        background: "#F6F4E5",
        customClass: {
          popup: "rounded-2xl shadow-xl border border-[#10333B]/10 !w-[95vw] sm:!w-[32rem]",
        },
        width: "95vw",
        padding: "0.75rem",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    }
  }, [showRating, backendConversationId]);

  useEffect(() => {
    fetch("/animations/ai-generation.json")
      .then((res) => {
        if (!res.ok) {
          return fetch("https://lottie.host/embed/xsEjMdVOJN").then((lottieRes) => {
            if (!lottieRes.ok) {
              throw new Error("Failed to load from both local and CDN");
            }
            return lottieRes.json();
          });
        }
        return res.json();
      })
      .then((data) => setAnimationData(data))
      .catch((err) => {
        console.error("Failed to load Lottie animation:", err);
        console.log(
          "To fix: Download the JSON from https://lottiefiles.com/animations/ai-generation-xsEjMdVOJN"
        );
        console.log("Save it as: public/animations/ai-generation.json");
      });
  }, []);

  if (shouldShowLoader) return <AppLoadingScreen />;

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-start pt-4 sm:pt-6 md:pt-8 px-4 sm:px-6 md:px-8 pb-8"
      style={{
        background: "linear-gradient(180deg, #F8F4C5 0%, #E4C5FF 49.52%, #F8F4C5 100%)",
      }}
    >
      <div className="w-full flex justify-center mb-8 sm:mb-12 md:mb-16">
        <div className="flex items-center gap-1">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-5 h-5 sm:w-6 sm:h-6 relative">
                <img src={profoundLogo} alt="Profound Logo" className="object-contain w-full h-full" />
              </div>
            </div>
          </div>
          <div className="h-5 sm:h-6 w-auto">
            <img
              src={profoundWord}
              alt="Profound"
              width={105}
              height={22}
              className="h-full w-auto"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col items-center w-full max-w-2xl">
        {/* Lottie Animation */}
        <div className="mb-6 sm:mb-8 flex items-center justify-center">
          <div className="w-40 h-32 sm:w-48 sm:h-40">
            {animationData ? (
              <Lottie
                animationData={animationData}
                loop={true}
                autoplay={true}
                style={{ width: "100%", height: "100%" }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        </div>

        {/* Main Heading */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          {!isVerificationFailed && (
            <>
              <h1
                className="text-3xl sm:text-4xl md:text-5xl font-normal leading-tight mb-2"
                style={{
                  fontFamily: "Inter, sans-serif",
                  color: "#1D1D1D",
                  letterSpacing: "-0.02em",
                }}
              >
                Thank you for speaking to Profound.
              </h1>
            </>
          )}
        </div>

        {/* Primary Information Box */}
        <div
          className="w-full rounded-[32px] p-[24px] relative"
          style={{
            backgroundColor: "transparent",
            border: "1px solid white",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <div className="flex items-center gap-4 sm:gap-5">
            <div className="flex-shrink-0 flex items-center justify-center bg-black/5 p-3 rounded-full">
              <span
                className="material-icons"
                style={{
                  fontSize: "24px",
                  color: "#1D1D1D",
                  display: "inline-block",
                }}
              >
                mail_outline
              </span>
            </div>

            {/* Text Content */}
            <div className="flex-1 space-y-2">
              {isVerificationFailed ? (
                <p
                  className="text-base sm:text-lg md:text-xl font-normal"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    color: "#1D1D1D",
                    lineHeight: "1.5",
                  }}
                >
                  Seems like you do not have access to Profound yet. We have added you to the
                  waitlist and will onboard you very soon
                </p>
              ) : (
                <p
                  className="text-base sm:text-lg md:text-xl font-normal"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    color: "#1D1D1D",
                    lineHeight: "1.5",
                  }}
                >
                  Your story is being woven into something remarkable. Look for it in your inbox
                  soon - it'll be worth the wait
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
